import { Component, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { BackNavigationService } from 'src/app/services/back-navigation.service';
import { NavigationTrackingService, NavigationTriggerType } from 'src/app/services/navigation-tracking.service';

/**
 * Global back button pinned to the top-left of the screen, on the same line as the top-right
 * header icons. Rendered once in the app shell (app.component.html) beside the nav shells, so it
 * appears on every internal route — including ones without the header-icon line. Hidden on the
 * section landing hubs. Target resolution lives in BackNavigationService.
 */
@Component({
  selector: 'app-back-button',
  templateUrl: './back-button.component.html',
  styleUrls: ['./back-button.component.scss'],
  standalone: true,
  imports: [CommonModule, AngularSvgIconModule],
})
export class BackButtonComponent implements OnDestroy {
  show = false;

  private subscriptions = new Subscription();

  constructor(
    private router: Router,
    private backNavigation: BackNavigationService,
    private navigationTracking: NavigationTrackingService,
  ) {
    this.updateVisibility();
    const sub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.updateVisibility());
    this.subscriptions.add(sub);
  }

  private currentPath(): string {
    return this.router.url.split('?')[0].split('#')[0];
  }

  private updateVisibility(): void {
    this.show = !this.backNavigation.isBackButtonHidden(this.currentPath());
  }

  goBack(): void {
    const fromScreen = this.currentPath();
    const toScreen = this.backNavigation.resolveTarget(fromScreen);
    this.navigationTracking.track({
      fromScreen,
      toScreen,
      triggerType: NavigationTriggerType.InPageNav,
      eventName: 'BackButtonClicked',
    });
    this.router.navigateByUrl(toScreen);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }
}
